/**
 * ProjectHeader is one project: who it is, what it costs, and who hosts it.
 *
 * It is rendered twice — as the card in the dashboard's project list and as the
 * header of the project screen — which is why it lives here rather than in
 * either screen. The project screen is that card opened, not a second opinion
 * about it.
 *
 * Props:
 *   project — { name, url, feedCount, providerCount, mode,
 *               providers: [{ provider, feeds, changes, polls, changeRatio,
 *                             lastChangeLabel, lastChangedAt, filterable }],
 *               responseTimes: { samples, avgMs, p95Ms } }
 *   onOpen — the project itself; omitted on the project screen, where we are
 *     already inside it
 *   onOpenProvider(provider) — omitted for the overflow group
 */
export default function ProjectHeader({ project, onOpen, onOpenProvider }) {
  return (
    <section
      className="ical-card"
      style={{ background: "var(--surface-0)", border: "1px solid var(--border)", padding: 0 }}
    >
      <div
        onClick={onOpen}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "var(--space-4)",
          cursor: onOpen ? "pointer" : "default",
        }}
      >
        <ProjectMark name={project?.name} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: "var(--text-lg)", fontWeight: 500 }}>{project?.name}</p>
          <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            {project?.feedCount?.toLocaleString()} feeds · {project?.providerCount ?? 0} providers
            {project?.url ? ` · ${project.url}` : ""}
          </p>
        </div>
        {project?.mode ? (
          <span className={`ical-badge ${project.mode === "downtime" ? "ical-badge-neutral" : "ical-badge-success"}`}>
            {project.mode === "downtime" ? "Downtime" : "Active"}
          </span>
        ) : null}
      </div>

      {/*
        A row with no number behind it is omitted rather than printed as "—": the
        project screen's detail read does not carry a change count, and an empty
        row reads as a project that never changed.
      */}
      {project?.changes24h == null ? null : (
        <StatRow label="Changes · 24h" value={project.changes24h} />
      )}
      <LatencyRow label="Response time · 24h" responseTimes={project?.responseTimes} />

      {project?.providers?.length > 0 && (
        <div style={{ borderTop: "1px solid var(--border)", padding: "var(--space-3) var(--space-4) var(--space-4)" }}>
          <p style={labelStyle}>Providers · 28d</p>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {project.providers.map((provider) => (
              <ProviderRow
                key={provider.provider}
                provider={provider}
                onOpen={provider.filterable && onOpenProvider ? () => onOpenProvider(provider.provider) : undefined}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

const labelStyle = {
  margin: "0 0 8px",
  fontSize: "var(--text-xs)",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
};

/**
 * ProjectMark is the square initial beside a project name.
 *
 * Square with a small radius, white on the panel's accent blue — the same blue
 * every primary button uses. Square because a project is a *thing*, and the
 * providers inside it use the round shape: the two levels of the hierarchy are
 * then distinguishable without reading a label.
 */
export function ProjectMark({ name, size = 36 }) {
  const letter = (name ?? "").trim().charAt(0).toUpperCase() || "?";
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: "var(--radius)",
        display: "grid",
        placeItems: "center",
        background: "var(--fill-primary)",
        color: "var(--on-primary)",
        fontWeight: 600,
        fontSize: size >= 36 ? "var(--text-lg)" : "var(--text-sm)",
      }}
    >
      {letter}
    </span>
  );
}

/**
 * ProviderAvatar is the round initial beside a provider name.
 *
 * Round on a light tint of the accent — the panel's avatar shape everywhere
 * else. Deliberately not the project's square blue: a provider is one of several
 * inside a project, and giving both levels the same weight made the list read as
 * a column of projects.
 */
export function ProviderAvatar({ name, size = 36 }) {
  const letter = (name ?? "").trim().charAt(0).toUpperCase() || "?";
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        background: "var(--bg-accent)",
        color: "var(--text-accent)",
        fontWeight: 600,
        fontSize: size >= 36 ? "var(--text-lg)" : "var(--text-sm)",
      }}
    >
      {letter}
    </span>
  );
}

/**
 * latencyText renders a response-time summary for a caller that wants one line.
 *
 * p95 is printed with "≤" because that is what the storage supports: latencies
 * are kept in a fixed histogram, so the 95th percentile is known to within a
 * bucket and the answer is a ceiling. Printing it with "=" would claim a
 * measurement that was never taken.
 */
export function latencyText(responseTimes) {
  if (!responseTimes || !responseTimes.samples) return "—";
  const avg = `${responseTimes.avgMs} ms`;
  return responseTimes.p95Ms == null ? avg : `${avg} avg · ≤ ${responseTimes.p95Ms} ms p95`;
}

/**
 * LatencyRow is the response-time summary, laid out as two labelled lines.
 *
 * Deliberately *not* one big number in the same size as the change count: a
 * latency with no qualifier beside a change count reads as the most important
 * figure on the card, and it is not. Both numbers are the same size as each
 * other and one step down from a headline, and each carries its own label —
 * "on average" and "p95" — because an unlabelled pair of milliseconds is a
 * question rather than an answer.
 */
function LatencyRow({ label, responseTimes }) {
  const measured = responseTimes?.samples > 0;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px var(--space-4)",
        borderTop: "1px solid var(--border)",
      }}
    >
      <span style={labelStyle}>{label}</span>
      {measured ? (
        <div style={{ textAlign: "right" }}>
          <p style={latencyLine}>
            <span style={latencyQualifier}>on average</span>
            <span style={latencyValue}>{responseTimes.avgMs} ms</span>
          </p>
          <p style={{ ...latencyLine, marginTop: 2 }}>
            <span style={latencyQualifier}>p95</span>
            <span style={latencyValue}>
              {responseTimes.p95Ms == null ? "—" : `≤ ${responseTimes.p95Ms} ms`}
            </span>
          </p>
        </div>
      ) : (
        <span style={{ fontSize: "var(--text-base)", color: "var(--text-muted)" }}>—</span>
      )}
    </div>
  );
}

const latencyLine = { margin: 0, display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 8 };
const latencyQualifier = { fontSize: "var(--text-sm)", color: "var(--text-secondary)" };
const latencyValue = { fontSize: "var(--text-lg)", fontWeight: 600 };

/**
 * lastChangeTone colours how long ago a provider last changed.
 *
 * The thresholds are about attention, not health: something that moved within
 * the hour is the integration working, a few hours is normal, a day is quiet,
 * and beyond that a calendar that has not changed at all is worth a second look
 * — it is the shape a broken feed makes, because a feed that stopped being read
 * also stops changing. `at` is empty for a provider that never changed, which
 * gets the muted grey rather than the red: "never in the window" is a statement
 * about how long we have watched, not evidence of a fault.
 */
export function lastChangeTone(at, now = new Date()) {
  if (!at) return { color: "var(--text-muted)" };
  const instant = new Date(at);
  if (Number.isNaN(instant.getTime())) return { color: "var(--text-muted)" };

  const hours = (now.getTime() - instant.getTime()) / 3_600_000;
  if (hours < 1) return { color: "var(--text-success)" };
  if (hours < 3) return { color: "var(--text-warning)" };
  if (hours < 24) return { color: "var(--text-secondary)" };
  return { color: "var(--text-danger)" };
}

/**
 * ProviderRow is one calendar service inside a project.
 *
 * No frame: the row is a line of text with its figures pushed to the right, and
 * on a project with a dozen providers a box around each one is a dozen extra
 * lines for no information. The name carries the row; everything else is
 * supporting detail and is aligned so the eye can compare down the column.
 *
 * A row without `onOpen` is the overflow group: it folds several unrelated
 * providers together, so there is no hostname to filter the feed list by, and a
 * link would promise a view that cannot exist.
 */
function ProviderRow({ provider, onOpen }) {
  return (
    <div
      className="ical-provider-row"
      onClick={onOpen}
      style={{ cursor: onOpen ? "pointer" : "default" }}
    >
      <ProviderAvatar name={provider.provider} size={28} />
      <span className="ical-provider-name">{provider.provider}</span>

      {/*
        The figures travel as one group. On a wide row they sit at the right edge
        because margin-left:auto pushes them there; on a narrow one the group
        drops to its own line whole, instead of the five items wrapping one at a
        time into an arrangement nobody designed.
      */}
      <span className="ical-provider-figures">
        <span className="ical-provider-feeds">
          {provider.feeds?.toLocaleString()}
          <span className="ical-provider-feeds-unit"> feeds</span>
        </span>
        <span className="ical-provider-change" style={lastChangeTone(provider.lastChangedAt)}>
          {provider.lastChangeLabel}
        </span>
        <span className="ical-badge ical-badge-neutral ical-provider-ratio">
          {provider.changeRatio == null ? "—" : `${(provider.changeRatio * 100).toFixed(1)}% chg/poll`}
        </span>
      </span>

      {onOpen ? <span style={{ color: "var(--text-muted)" }}>›</span> : <span style={{ width: 8 }} />}
    </div>
  );
}

/** StatRow is one "label on the left, number on the right" line of a card. */
function StatRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px var(--space-4)",
        borderTop: "1px solid var(--border)",
      }}
    >
      <span style={labelStyle}>{label}</span>
      <span style={{ fontSize: "var(--text-xl)", fontWeight: 600 }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </span>
    </div>
  );
}
